import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom'
import { Layout } from '../components/Layout'
import { EmConstrucao } from '../features/EmConstrucao'
import { PaginaReferencias } from '../features/admin/referencias/PaginaReferencias'
import { PaginaAdminUsuarios } from '../features/admin/usuarios/PaginaAdminUsuarios'
import { PaginaUsoDaIa } from '../features/admin/uso-da-ia/PaginaUsoDaIa'
import { PaginaCreditoDaIa } from '../features/admin/credito-da-ia/PaginaCreditoDaIa'
import { PaginaLoteDeAnalise } from '../features/admin/lotes/PaginaLoteDeAnalise'
import { PaginaNaoEncontrada } from '../features/PaginaNaoEncontrada'
import { PaginaDetalheChamado } from '../features/chamados/detalhe/PaginaDetalheChamado'
import { PaginaFechamento } from '../features/chamados/fiscalizacao/PaginaFechamento'
import { PaginaListaChamados } from '../features/chamados/lista/PaginaListaChamados'
import { PaginaIad } from '../features/iad/PaginaIad'
import { PaginaSerieDeIndicadores } from '../features/indicadores/PaginaSerieDeIndicadores'
import { PaginaRelatorios } from '../features/relatorios/PaginaRelatorios'
import { PaginaAtividadeDeRepositorios } from '../features/repositorios/PaginaAtividadeDeRepositorios'
import { PaginaConsulta } from '../features/consulta/PaginaConsulta'
import { PaginaDocumentos } from '../features/documentos/PaginaDocumentos'
import { PaginaDetalheGmud } from '../features/gmud/detalhe/PaginaDetalheGmud'
import { PaginaListaGmuds } from '../features/gmud/lista/PaginaListaGmuds'
import { PaginaNovaGmud } from '../features/gmud/novo/PaginaNovaGmud'
import { PaginaQuadroGmuds } from '../features/gmud/quadro/PaginaQuadroGmuds'
import { PaginaDocumentoOs } from '../features/ordens-servico/documento/PaginaDocumentoOs'
import { PaginaOrdemDeServico } from '../features/ordens-servico/detalhe/PaginaOrdemDeServico'
import { PaginaOrdensDeServico } from '../features/ordens-servico/lista/PaginaOrdensDeServico'
import { PaginaRegistrarOs } from '../features/ordens-servico/nova/PaginaRegistrarOs'
import { PaginaRelatorioRdm } from '../features/gmud/relatorio/PaginaRelatorioRdm'
import { PaginaTrp } from '../features/relatorios/trp/PaginaTrp'
import { PaginaCab } from '../features/cab/PaginaCab'
import { PaginaCalendarioDoComite } from '../features/cab/PaginaCalendarioDoComite'
import { PaginaPainel } from '../features/painel/PaginaPainel'
import { PaginaPerfil } from '../features/perfil/PaginaPerfil'
import { PaginaLogin } from '../features/sessao/PaginaLogin'
import { RotaDoContratante } from '../features/sessao/RotaDoContratante'
import { RotaProtegida } from '../features/sessao/RotaProtegida'
import { caminhoDe, itensEmConstrucao } from '../lib/navegacao'

/**
 * Uma rota para cada item do menu que ainda não tem tela, montada a partir do registro.
 *
 * Escritas à mão, esta lista e o menu divergiriam no primeiro item novo — e o jeito como a
 * divergência aparece é o pior possível: o item continua no menu, o clique cai no "endereço não
 * existe", e quem lê a tela conclui que o sistema quebrou. Aqui, acrescentar item ao registro é
 * ganhar a rota junto.
 */
const ROTAS_EM_CONSTRUCAO: RouteObject[] = itensEmConstrucao().map((item) => ({
  path: caminhoDe(item),
  element: <EmConstrucao item={item} />,
}))

/**
 * As rotas da aplicação, em três níveis.
 *
 * A proteção é a POSIÇÃO na árvore, não uma linha que se lembra de escrever: fora do `/login`,
 * nada existe fora de `RotaProtegida`. Dentro dela há três moldagens, e é por isso que o `Layout` é
 * um nível próprio — documento de impressão não pode sair com menu, cabeçalho e rodapé no papel,
 * mas continua sendo tela de usuário logado. As três: com layout; impressão aberta aos dois lados
 * do contrato (o RDM, que o executor também imprime); e impressão restrita ao contratante (o termo
 * de recebimento, que é ato da fiscalização).
 */
export const router = createBrowserRouter([
  { path: '/login', element: <PaginaLogin /> },
  {
    element: <RotaProtegida />,
    children: [
      { path: '/', element: <Navigate to="/painel" replace /> },
      {
        element: <Layout />,
        children: [
          { path: '/painel', element: <PaginaPainel /> },
          { path: '/gmuds', element: <PaginaListaGmuds /> },
          { path: '/gmuds/novo', element: <PaginaNovaGmud /> },
          // FORA do `RotaDoContratante`, ao lado de `/gmuds`: o quadro mostra o processo de
          // mudanças, que é dos dois lados do contrato. Quem executa a mudança precisa enxergar
          // onde ela parou tanto quanto quem a fiscaliza.
          { path: '/gmuds/quadro', element: <PaginaQuadroGmuds /> },
          { path: '/gmuds/:id', element: <PaginaDetalheGmud /> },
          // Também fora do portão: a ordem de serviço OBRIGA a contratada. O prazo corre da entrega
          // ao preposto (6.24.6, p.45) e a indisponibilidade da equipe ao recebê-la vale 10 pontos
          // de IAD (ANEXO VIII, item 4, p.157) — esconder a ordem de quem ela obriga seria esconder
          // a obrigação. Quem só lê continua só lendo: registrar e revogar são do FISCAL, no servidor.
          { path: '/ordens-servico', element: <PaginaOrdensDeServico /> },
          { path: '/ordens-servico/nova', element: <PaginaRegistrarOs /> },
          { path: '/ordens-servico/:id', element: <PaginaOrdemDeServico /> },
          { path: '/perfil', element: <PaginaPerfil /> },
          // O calendário fica FORA do portão de contratante, ao contrário de `/cab`: é o prazo que
          // o executor tem de cumprir, e escondê-lo dele seria esconder a própria obrigação.
          { path: '/cab/calendario', element: <PaginaCalendarioDoComite /> },
          // Dentro da moldura, e não em volta dela: a recusa tem de sair com menu e cabeçalho,
          // para a pessoa enxergar de imediato para onde pode ir. Tela de recusa sem saída é um
          // beco — e o beco leva a um chamado no suporte.
          {
            element: <RotaDoContratante />,
            children: [
              // As rotas de chamado vêm ANTES das de "em construção" por segurança, não por
              // estilo: o registro de navegação é a fonte das duas listas, e um item que voltasse
              // a `em-construcao` por engano geraria aqui uma rota concorrente com a tela pronta.
              // Com a tela declarada primeiro, o pior caso é um item de menu desatualizado — não
              // uma tela pronta substituída por um aviso de obra.
              { path: '/chamados', element: <PaginaListaChamados /> },
              { path: '/chamados/:id', element: <PaginaDetalheChamado /> },
              { path: '/fiscalizacao', element: <PaginaFechamento /> },
              { path: '/indicadores', element: <PaginaSerieDeIndicadores /> },
              { path: '/relatorios', element: <PaginaRelatorios /> },
              { path: '/repositorios', element: <PaginaAtividadeDeRepositorios /> },
              { path: '/consulta', element: <PaginaConsulta /> },
              { path: '/consulta/:id', element: <PaginaConsulta /> },
              { path: '/documentos', element: <PaginaDocumentos /> },
              { path: '/iad', element: <PaginaIad /> },
              { path: '/cab', element: <PaginaCab /> },
              { path: '/admin/usuarios', element: <PaginaAdminUsuarios /> },
              { path: '/admin/referencias', element: <PaginaReferencias /> },
              { path: '/admin/uso-da-ia', element: <PaginaUsoDaIa /> },
              { path: '/admin/credito-da-ia', element: <PaginaCreditoDaIa /> },
              { path: '/admin/lotes-de-analise', element: <PaginaLoteDeAnalise /> },
              ...ROTAS_EM_CONSTRUCAO,
            ],
          },
        ],
      },
      { path: '/gmuds/:id/relatorio', element: <PaginaRelatorioRdm /> },
      // Impressão fora do `Layout` como o RDM — e, ao contrário do TRP, fora do portão de
      // contratante: o executor precisa poder imprimir o documento que o obriga.
      { path: '/ordens-servico/:id/documento', element: <PaginaDocumentoOs /> },
      // O termo de recebimento é impressão COMO o RDM — fora do Layout, para não sair com menu no
      // papel — mas, ao contrário dele, é da fiscalização: quem é medido não emite o próprio termo.
      // Daí o portão dentro do nível de impressão, que antes não existia na árvore.
      {
        element: <RotaDoContratante />,
        children: [{ path: '/relatorios/trp/:competencia', element: <PaginaTrp /> }],
      },
    ],
  },
  { path: '*', element: <PaginaNaoEncontrada /> },
])
